/**
 * 🔌 Geräte- & Lager-Scanner-Service (Meine Geräte, Live-Handys, Drucker-Verbindungen, Zebra TC75 & Ringscanner)
 */
import { apiFetch, isLoggedIn } from './apiService';

const CLIENT_ID_KEY = 'kiosk_bg_client_id';
const CLIENT_NAME_KEY = 'kiosk_client_device_name';
const PRINT_BRIDGE_HOST_KEY = 'kiosk_print_bridge_host';
const REMOTE_PRINT_CLIENT_KEY = 'kiosk_remote_print_client';
const REMOTE_PRINTER_NAME_KEY = 'kiosk_remote_printer_name';
const REMOTE_PRINTER_ADDR_KEY = 'kiosk_remote_printer_addr';

export function getClientDeviceId() {
  try {
    let cid = localStorage.getItem(CLIENT_ID_KEY);
    if (!cid) {
      cid = 'app_' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem(CLIENT_ID_KEY, cid);
    }
    return cid;
  } catch {
    return 'app_default';
  }
}

export function detectClientPlatform() {
  try {
    const ua = navigator.userAgent || '';
    if (/TC75|TC5\d|MC3\d|Zebra/i.test(ua)) return 'scanner';
    if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
    if (/iPhone|iPod/i.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return 'pc';
  } catch {
    return 'android';
  }
}

const KNOWN_MODELS = {
  'SM-S908B': 'Samsung S22 Ultra (SM-S908B)',
  'SM-S918B': 'Samsung S23 Ultra (SM-S918B)',
  'SM-S928B': 'Samsung S24 Ultra (SM-S928B)',
  'SM-S938B': 'Samsung S25 Ultra (SM-S938B)',
  'A015': 'CMF Phone 2 Pro (A015)',
  'UTBOOK_15_EEA': 'Shop-Tablet (UTBook 15)',
};

function isGenericName(name) {
  const s = String(name || '').trim();
  if (!s) return true;
  return /^(handy|android handy|samsung handy|pixel handy|xiaomi handy|tablet|pc \/ browser|mac \/ browser)$/i.test(s);
}

export function getClientDeviceName() {
  try {
    const saved = localStorage.getItem(CLIENT_NAME_KEY);
    if (saved && saved.trim() && !isGenericName(saved.trim())) return saved.trim();
    const ua = navigator.userAgent || '';
    const androidMatch = ua.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|\))/i);
    if (androidMatch && androidMatch[1]) {
      const m = androidMatch[1].trim();
      const upper = m.toUpperCase();
      if (KNOWN_MODELS[upper]) return KNOWN_MODELS[upper];
      if (/^SM-/i.test(m)) return `Samsung (${m})`;
      if (/TC75|TC5\d|TC2\d|MC3\d/i.test(m)) return `Zebra (${m})`;
      if (!/Mobile/i.test(ua)) return `Android Tablet (${m})`;
      return `Android Handy (${m})`;
    }
    if (/TC75/i.test(ua)) return 'Zebra TC75';
    if (/Samsung|SM-[A-Z0-9]+/i.test(ua)) return 'Samsung Handy';
    if (/Pixel/i.test(ua)) return 'Pixel Handy';
    if (/Xiaomi|Redmi|POCO/i.test(ua)) return 'Xiaomi Handy';
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android Handy';
    if (/Macintosh|Mac OS/i.test(ua)) return 'Mac / Browser';
    if (/Windows/i.test(ua)) return 'PC / Browser';
    return 'Handy';
  } catch {
    return 'Handy';
  }
}

// 🔋 Live-Batteriestatus des Geräts (via Web Battery API + optional Android BtPrint.getThermal)
let cachedLocalBattery = null;
let batteryListenerAttached = false;
let cachedBondedDevices = [];

export async function readLocalBatteryStatus() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.getBattery === 'function') {
      const b = await navigator.getBattery();
      if (b) {
        const updateFromMgr = () => {
          const lvl = Number.isFinite(b.level) ? Math.round(b.level * 100) : null;
          if (lvl != null && lvl >= 0 && lvl <= 100) {
            cachedLocalBattery = {
              ...(cachedLocalBattery || {}),
              level: lvl,
              charging: Boolean(b.charging),
            };
          }
        };
        updateFromMgr();
        if (!batteryListenerAttached) {
          batteryListenerAttached = true;
          try {
            b.addEventListener('levelchange', updateFromMgr);
            b.addEventListener('chargingchange', updateFromMgr);
          } catch { /* ignore */ }
        }
      }
    }
  } catch { /* ignore */ }

  try {
    const btPrint = typeof window !== 'undefined' && window.Capacitor?.Plugins?.BtPrint;
    if (btPrint && typeof btPrint.getThermal === 'function') {
      const th = await btPrint.getThermal();
      if (th) {
        cachedLocalBattery = {
          ...(cachedLocalBattery || {}),
          ...(Number.isFinite(Number(th.batteryLevel)) ? { level: Math.round(Number(th.batteryLevel)) } : {}),
          ...(typeof th.charging === 'boolean' ? { charging: th.charging } : {}),
          ...(Number.isFinite(Number(th.batteryTempC)) ? { tempC: Math.round(Number(th.batteryTempC) * 10) / 10 } : {}),
        };
      }
    }
  } catch { /* ignore */ }

  return cachedLocalBattery;
}

export async function readBondedBluetoothDevices() {
  try {
    const btPrint = typeof window !== 'undefined' && window.Capacitor?.Plugins?.BtPrint;
    if (btPrint && typeof btPrint.getBtStatus === 'function') {
      const st = await btPrint.getBtStatus();
      if (st && Array.isArray(st.devices)) {
        cachedBondedDevices = st.devices.map((d) => ({
          name: d.name || d.address || 'Bluetooth-Gerät',
          address: d.address || '',
          connected: Boolean(d.connected),
          ...(Number.isFinite(Number(d.batteryLevel)) && Number(d.batteryLevel) >= 0 && Number(d.batteryLevel) <= 100
            ? { batteryLevel: Math.round(Number(d.batteryLevel)) }
            : {}),
        }));
      }
    }
  } catch { /* ignore */ }
  return cachedBondedDevices;
}

export function setClientDeviceName(name) {
  try {
    const clean = String(name || '').trim().slice(0, 80);
    if (clean) localStorage.setItem(CLIENT_NAME_KEY, clean);
    else localStorage.removeItem(CLIENT_NAME_KEY);
  } catch { /* ignore */ }
}

export function isPrintBridgeHostEnabled() {
  try {
    const v = localStorage.getItem(PRINT_BRIDGE_HOST_KEY);
    if (v === '0') return false;
    if (v === '1') return true;
    // Standardmäßig aktiv, sobald ein physischer Drucker (Bluetooth/WLAN) an diesem Handy eingerichtet ist
    const raw = localStorage.getItem('kiosk_label_printer');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.address && parsed.address !== 'remote-bridge' && !parsed.remote) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

export function setPrintBridgeHostEnabled(enabled) {
  try {
    localStorage.setItem(PRINT_BRIDGE_HOST_KEY, enabled ? '1' : '0');
  } catch { /* ignore */ }
}

export function isRemotePrintClientEnabled() {
  try { return localStorage.getItem(REMOTE_PRINT_CLIENT_KEY) === '1'; } catch { return false; }
}

export function setRemotePrintClientEnabled(enabled, targetPrinter = null) {
  try {
    if (enabled) {
      localStorage.setItem(REMOTE_PRINT_CLIENT_KEY, '1');
      if (targetPrinter?.name) localStorage.setItem(REMOTE_PRINTER_NAME_KEY, targetPrinter.name);
      if (targetPrinter?.address) localStorage.setItem(REMOTE_PRINTER_ADDR_KEY, targetPrinter.address);
    } else {
      localStorage.removeItem(REMOTE_PRINT_CLIENT_KEY);
      localStorage.removeItem(REMOTE_PRINTER_NAME_KEY);
      localStorage.removeItem(REMOTE_PRINTER_ADDR_KEY);
    }
  } catch { /* ignore */ }
}

export function getLocalPhoneState() {
  const clientId = getClientDeviceId();
  const name = getClientDeviceName();
  const platform = detectClientPlatform();
  let printer = { connected: false };

  try {
    const rawPrinter = localStorage.getItem('kiosk_label_printer');
    const parsedPrinter = rawPrinter ? JSON.parse(rawPrinter) : null;
    const lang = localStorage.getItem('kiosk_label_lang') === 'zpl' ? 'zpl' : 'cpcl';
    const size = localStorage.getItem('kiosk_label_size') || '100x150';

    if (parsedPrinter?.address && parsedPrinter.address !== 'remote-bridge' && !parsedPrinter.remote) {
      const addr = String(parsedPrinter.address);
      const isWifi = /^tcp:\/\//i.test(addr) || /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(addr) || parsedPrinter.type === 'tcp';
      const bondedMatch = cachedBondedDevices.find(
        (d) => d.address && String(d.address).toUpperCase() === addr.trim().toUpperCase()
      );
      printer = {
        connected: true,
        mode: isWifi ? 'wifi' : 'bluetooth',
        name: parsedPrinter.name || addr,
        address: addr,
        lang,
        size,
        bridgeHost: isPrintBridgeHostEnabled(),
        remoteClient: false,
        ...(bondedMatch && Number.isFinite(bondedMatch.batteryLevel)
          ? { battery: { level: bondedMatch.batteryLevel } }
          : {}),
      };
    } else if (isRemotePrintClientEnabled()) {
      printer = {
        connected: true,
        mode: 'remote_bridge',
        name: localStorage.getItem(REMOTE_PRINTER_NAME_KEY) || 'Etikettendrucker (Gleichzeitig verbunden)',
        address: localStorage.getItem(REMOTE_PRINTER_ADDR_KEY) || 'remote-bridge',
        lang,
        size,
        bridgeHost: false,
        remoteClient: true,
      };
    }
  } catch { /* ignore */ }

  return {
    clientId,
    name,
    platform,
    printer,
    battery: cachedLocalBattery,
    bondedDevices: cachedBondedDevices,
  };
}

const ACTIVE_COUNT_KEY = 'kiosk_active_device_count';

export function getActiveDeviceCount() {
  try {
    const raw = parseInt(localStorage.getItem(ACTIVE_COUNT_KEY) || '0', 10);
    const localState = getLocalPhoneState();
    const localMin = localState.printer?.connected ? 2 : 1;
    return Number.isFinite(raw) && raw > 0 ? Math.max(raw, localMin) : localMin;
  } catch {
    return 1;
  }
}

function updateStoredActiveDeviceCount(count) {
  if (!Number.isFinite(count) || count < 1) return;
  try {
    const localMin = getLocalPhoneState().printer?.connected ? 2 : 1;
    const effective = Math.max(count, localMin);
    localStorage.setItem(ACTIVE_COUNT_KEY, String(effective));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('kiosk:active-devices-changed', { detail: { count: effective } }));
    }
  } catch { /* ignore */ }
}

let lastHeartbeatAt = 0;
let lastHeartbeatPrinterAddr = '';
let lastHeartbeatResponse = null;

export async function sendDeviceHeartbeat(extra = {}) {
  try {
    await Promise.all([
      readLocalBatteryStatus(),
      readBondedBluetoothDevices(),
    ]);
    const localState = getLocalPhoneState();
    const localMin = localState.printer?.connected ? 2 : 1;
    if (!isLoggedIn()) {
      updateStoredActiveDeviceCount(localMin);
      return null;
    }

    const now = Date.now();
    const curPrinterAddr = `${localState.printer?.connected ? '1' : '0'}:${localState.printer?.address || ''}`;
    if (
      !extra.customName &&
      !extra.force &&
      now - lastHeartbeatAt < 8000 &&
      curPrinterAddr === lastHeartbeatPrinterAddr &&
      lastHeartbeatResponse
    ) {
      return lastHeartbeatResponse;
    }

    lastHeartbeatAt = now;
    lastHeartbeatPrinterAddr = curPrinterAddr;

    const payload = {
      ...localState,
      ...extra,
    };
    const res = await apiFetch('/devices/heartbeat', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    lastHeartbeatResponse = res;
    if (res?.phone?.name && !extra.customName) {
      setClientDeviceName(res.phone.name);
    }
    if (Number.isFinite(res?.activeDeviceCount)) {
      updateStoredActiveDeviceCount(res.activeDeviceCount);
    }
    if (res?.remoteWatch?.watched && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('kiosk:remote-control', {
        detail: {
          type: 'start_stream',
          silent: res.remoteWatch.silent !== false,
          clientId: localState.clientId,
        },
      }));
    }
    return res;
  } catch {
    return null;
  }
}

export async function fetchDevices() {
  const res = await apiFetch('/devices', { method: 'GET' });
  try {
    if (res) {
      const phonesCount = Array.isArray(res.phones) ? res.phones.length : 1;
      const scanners = Array.isArray(res.devices) ? res.devices.length : 0;
      const printerKeys = new Set();
      for (const p of (res.phones || [])) {
        if (p?.printer?.connected && !p.printer.remoteClient && p.printer.address && p.printer.address !== 'remote-bridge') {
          printerKeys.add(String(p.printer.address).trim().toUpperCase());
        }
      }
      const total = Math.max(phonesCount, 1) + printerKeys.size + scanners;
      updateStoredActiveDeviceCount(total);
    }
  } catch { /* ignore */ }
  return res;
}

export async function createDevice({ name, type = 'wlan_scanner', role = 'lager_einlagern', config = {} }) {
  return apiFetch('/devices', {
    method: 'POST',
    body: JSON.stringify({ name, type, role, config }),
  });
}

export async function updateDevice(id, patch) {
  return apiFetch(`/devices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function deleteDevice(id) {
  return apiFetch(`/devices/${id}`, {
    method: 'DELETE',
  });
}

export async function submitWarehouseScan({ code, deviceId = null, role = null, forceType = null, clearPending = false, reset = false, clientId = null, scanlog = null, inventory = null }) {
  return apiFetch('/devices/scan', {
    method: 'POST',
    body: JSON.stringify({
      code,
      ...(deviceId ? { deviceId } : {}),
      ...(role ? { role } : {}),
      ...(forceType ? { forceType } : {}),
      ...(clearPending || reset ? { reset: true, clearPending: true } : {}),
      ...(clientId ? { clientId } : { clientId: getClientDeviceId() }),
      ...(scanlog ? { scanlog } : {}),
      ...(inventory ? { inventory } : {}),
    }),
  });
}

export const sendDeviceScan = submitWarehouseScan;

export async function sendRemotePrintJob({ paket, copies = 1, code = null, targetDeviceId = null }) {
  return apiFetch('/devices/print', {
    method: 'POST',
    body: JSON.stringify({
      ...(paket ? { paket } : {}),
      ...(code ? { code } : {}),
      copies,
      ...(targetDeviceId ? { targetDeviceId } : {}),
      requestedBy: getClientDeviceName(),
    }),
  });
}

export const DEVICE_ROLES = [
  {
    id: 'lager_einlagern',
    labelDe: '📦📍 Lager: Paket → Regal (Regalplatz setzen/überschreiben)',
    labelEn: '📦📍 Warehouse: Parcel → Shelf (set/overwrite shelf)',
    labelNl: '📦📍 Magazijn: Pakket → Vak (vakplaats instellen)',
  },
  {
    id: 'lager_regal_zuerst',
    labelDe: '📍📦 Lager (Fach-Modus): 1× Regal → viele Pakete einbuchen',
    labelEn: '📍📦 Warehouse (Shelf-first): 1× Shelf → scan many parcels',
    labelNl: '📍📦 Magazijn (Vak-modus): 1× Vak → meerdere pakketten',
  },
  {
    id: 'annahme_stapel',
    labelDe: '📥 Annahme: Direkt in den Barcode-Stapel scannen',
    labelEn: '📥 Intake: Scan directly into barcode batch',
    labelNl: '📥 Aannemen: Direct in barcodestapel scannen',
  },
  {
    id: 'ausgabe_suche',
    labelDe: '📤 Ausgabe: Paket an der Theke suchen & öffnen',
    labelEn: '📤 Pickup: Search & open parcel at front desk',
    labelNl: '📤 Uitgifte: Pakket bij balie zoeken & openen',
  },
];

// 🔊 WebAudio-Signalgeber für das blinde Einlagern (ohne Blick aufs Display)
let sharedAudioCtx = null;

export function unlockWarehouseAudio() {
  try {
    if (typeof window === 'undefined') return null;
    if (!sharedAudioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) sharedAudioCtx = new AC();
    }
    if (sharedAudioCtx && sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume();
    }
    return sharedAudioCtx;
  } catch {
    return null;
  }
}

/**
 * Spielt ein unverwechselbares akustisches Signal für den blinden Lager-Modus:
 *   - 'package': 1x klarer hoher Ton (1050 Hz) -> Paket erkannt, wartet auf Regal-Scan
 *   - 'stored':  2x aufsteigender Erfolgs-Akkord (880 -> 1320 Hz) -> Regalplatz zugewiesen!
 *   - 'warning' / 'error': 2x tiefer Brummton (240 -> 185 Hz) -> Fehler / Erst Paket scannen!
 */
export function playWarehouseTone(type = 'package') {
  const ctx = unlockWarehouseAudio();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    if (type === 'package' || type === 'queued') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1050, now);
      gain.gain.setValueAtTime(0.35, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.17);
    } else if (type === 'stored') {
      [880, 1320].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + idx * 0.13);
        gain.gain.setValueAtTime(0.4, now + idx * 0.13);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.13 + 0.19);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + idx * 0.13);
        osc.stop(now + idx * 0.13 + 0.2);
      });
    } else {
      [240, 185].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, now + idx * 0.18);
        gain.gain.setValueAtTime(0.35, now + idx * 0.18);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.18 + 0.22);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + idx * 0.18);
        osc.stop(now + idx * 0.18 + 0.23);
      });
    }
  } catch { /* ignore audio errors */ }
}

export function speakWarehouseText(text, lang = 'de-DE') {
  try {
    if (typeof window === 'undefined' || !window.speechSynthesis || !text) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text));
    u.lang = lang;
    u.rate = 1.15;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}
